<script setup lang="ts">
/**
 * UiKitToastItem — отдельный тост. Обычно рендерится из UiKitToaster.
 * Figma: 3592-7897. Цвета только из токенов (классы hui-*).
 */
import { BG, TEXT_FG, type HuiColor } from './palette'
import type { KitToastAction, KitToastVariant } from './toast'

const props = withDefaults(
  defineProps<{
    color?: HuiColor
    variant?: KitToastVariant
    title?: string
    description?: string
    timeout?: number
    closable?: boolean
    action?: KitToastAction
  }>(),
  { color: 'default', variant: 'flat', title: '', description: '', timeout: 5000, closable: true, action: undefined },
)

const emit = defineEmits<{ (e: 'close'): void; (e: 'action'): void }>()

defineOptions({ name: 'UiKitToastItem' })

// Подложка тостов — ОПАКНАЯ (shade-100, не полупрозрачный /10): иначе сквозь тост
// просвечивает светлый объект под ним и цветной текст становится нечитаемым.
// shade-100 фон + shade-700 текст дают хороший контраст и корректно инвертируются в тёмной теме.
const FLAT: Record<HuiColor, string> = {
  default: 'bg-hui-content1 border border-hui-default-200 text-hui-foreground',
  primary: 'bg-hui-primary-100 border border-hui-primary-200 text-hui-primary-700',
  secondary: 'bg-hui-secondary-100 border border-hui-secondary-200 text-hui-secondary-700',
  success: 'bg-hui-success-100 border border-hui-success-200 text-hui-success-700',
  warning: 'bg-hui-warning-100 border border-hui-warning-200 text-hui-warning-700',
  danger: 'bg-hui-danger-100 border border-hui-danger-200 text-hui-danger-700',
}
const BORDERED: Record<HuiColor, string> = {
  default: 'bg-hui-content1 border-2 border-hui-default-300 text-hui-foreground',
  primary: 'bg-hui-content1 border-2 border-hui-primary text-hui-foreground',
  secondary: 'bg-hui-content1 border-2 border-hui-secondary text-hui-foreground',
  success: 'bg-hui-content1 border-2 border-hui-success text-hui-foreground',
  warning: 'bg-hui-content1 border-2 border-hui-warning text-hui-foreground',
  danger: 'bg-hui-content1 border-2 border-hui-danger text-hui-foreground',
}
const ICON_SOLID: Record<HuiColor, string> = {
  default: 'bg-hui-default-foreground/20 text-hui-default-foreground',
  primary: 'bg-hui-primary-foreground/20 text-hui-primary-foreground',
  secondary: 'bg-hui-secondary-foreground/20 text-hui-secondary-foreground',
  success: 'bg-hui-success-foreground/20 text-hui-success-foreground',
  warning: 'bg-hui-warning-foreground/20 text-hui-warning-foreground',
  danger: 'bg-hui-danger-foreground/20 text-hui-danger-foreground',
}
const ICON_COLOR: Record<HuiColor, string> = {
  default: 'bg-hui-default text-hui-default-foreground',
  primary: 'bg-hui-primary text-hui-primary-foreground',
  secondary: 'bg-hui-secondary text-hui-secondary-foreground',
  success: 'bg-hui-success text-hui-success-foreground',
  warning: 'bg-hui-warning text-hui-warning-foreground',
  danger: 'bg-hui-danger text-hui-danger-foreground',
}

const containerClass = computed(() => {
  if (props.variant === 'solid') return `${BG[props.color]} ${TEXT_FG[props.color]}`
  if (props.variant === 'bordered') return BORDERED[props.color]
  return FLAT[props.color]
})
const iconClass = computed(() => (props.variant === 'solid' ? ICON_SOLID[props.color] : ICON_COLOR[props.color]))
const iconKey = computed<'info' | 'success' | 'warning' | 'danger'>(() => {
  if (props.color === 'success') return 'success'
  if (props.color === 'warning') return 'warning'
  if (props.color === 'danger') return 'danger'
  return 'info'
})

// Авто-закрытие с паузой при наведении + прогресс-бар
const progress = ref(100)
let elapsed = 0
let startedAt = 0
let intervalId: ReturnType<typeof setInterval> | null = null

function stopTimer() {
  if (intervalId !== null) {
    clearInterval(intervalId)
    intervalId = null
  }
}
function runTimer() {
  if (props.timeout <= 0) return
  startedAt = Date.now() - elapsed
  intervalId = setInterval(() => {
    elapsed = Date.now() - startedAt
    progress.value = Math.max(0, 100 - (elapsed / props.timeout) * 100)
    if (elapsed >= props.timeout) {
      stopTimer()
      emit('close')
    }
  }, 50)
}
function pause() { stopTimer() }
function resume() { if (props.timeout > 0 && progress.value > 0) runTimer() }

onMounted(runTimer)
onBeforeUnmount(stopTimer)
</script>

<template>
  <div
    class="relative w-full sm:w-80 max-w-[calc(100vw-2rem)] flex items-start gap-3 p-4 rounded-hui-md shadow-lg overflow-hidden backdrop-blur-lg"
    :class="containerClass"
    role="status"
    @mouseenter="pause"
    @mouseleave="resume"
  >
    <span class="flex-none flex items-center justify-center w-6 h-6 mt-0.5 rounded-full" :class="iconClass">
      <svg class="w-3.5 h-3.5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
        <path v-if="iconKey === 'info'" fill-rule="evenodd" d="M10 18a8 8 0 1 0 0-16 8 8 0 0 0 0 16zM9 9a1 1 0 0 1 2 0v4a1 1 0 1 1-2 0V9zm1-4.25A1.25 1.25 0 1 0 10 7.25 1.25 1.25 0 0 0 10 4.75z" clip-rule="evenodd" />
        <path v-else-if="iconKey === 'success'" fill-rule="evenodd" d="M10 18a8 8 0 1 0 0-16 8 8 0 0 0 0 16zm3.7-9.3a1 1 0 0 0-1.4-1.4L9 10.58l-1.3-1.3a1 1 0 0 0-1.4 1.42l2 2a1 1 0 0 0 1.4 0l4-4z" clip-rule="evenodd" />
        <path v-else-if="iconKey === 'warning'" fill-rule="evenodd" d="M8.26 3.1c.77-1.33 2.71-1.33 3.48 0l6.04 10.45c.77 1.34-.2 3-1.74 3H3.96c-1.54 0-2.5-1.66-1.74-3L8.26 3.1zM10 7a1 1 0 0 0-1 1v3a1 1 0 1 0 2 0V8a1 1 0 0 0-1-1zm0 7.5a1.1 1.1 0 1 0 0-2.2 1.1 1.1 0 0 0 0 2.2z" clip-rule="evenodd" />
        <path v-else fill-rule="evenodd" d="M10 18a8 8 0 1 0 0-16 8 8 0 0 0 0 16zM10 5a1 1 0 0 1 1 1v4a1 1 0 1 1-2 0V6a1 1 0 0 1 1-1zm0 9.5a1.1 1.1 0 1 0 0-2.2 1.1 1.1 0 0 0 0 2.2z" clip-rule="evenodd" />
      </svg>
    </span>

    <div class="min-w-0 flex-1">
      <p v-if="title" class="text-sm font-medium leading-5">{{ title }}</p>
      <p v-if="description" class="text-[0.8125rem] leading-5 opacity-80" :class="{ 'mt-0.5': title }">{{ description }}</p>
      <button
        v-if="action"
        type="button"
        class="mt-2 inline-flex items-center gap-1 rounded-hui-sm px-2.5 py-1 text-[0.8125rem] font-medium bg-current/10 hover:bg-current/20 transition-colors outline-none focus-visible:ring-2 focus-visible:ring-hui-focus"
        @click="emit('action')"
      >
        {{ action.label }}
      </button>
    </div>

    <button
      v-if="closable"
      type="button"
      class="flex-none -mr-1 -mt-1 inline-flex items-center justify-center w-7 h-7 rounded-hui-sm opacity-70 hover:opacity-100 transition-opacity outline-none focus-visible:ring-2 focus-visible:ring-hui-focus"
      aria-label="Закрыть"
      @click="emit('close')"
    >
      <svg class="w-4 h-4" viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
        <path d="M6 6l8 8M14 6l-8 8" stroke-linecap="round" />
      </svg>
    </button>

    <span
      v-if="timeout > 0"
      class="absolute left-0 bottom-0 h-0.5 bg-current opacity-30"
      :style="{ width: `${progress}%` }"
    />
  </div>
</template>
